/**
 * Operations widgets — caisse, withholding, clients, suppliers, representatives.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClients } from '@/hooks/use-clients';
import { useSuppliers } from '@/hooks/use-suppliers';
import { useRepresentatives } from '@/hooks/use-representatives';
import { useOpenCashSessions, useCashSessions } from '@/hooks/use-caisse';
import { useWithholdingCertificates, useWithholdingDeclarations } from '@/hooks/use-withholding';
import { Widget, BigStat, MiniDonut, fmtMoney, fmtCount, isoMonthStart } from './common';

// ==================== CLIENTS / SUPPLIERS / REPS ====================

// ---- Clients by type (company vs individual) ----

export function ClientsByTypeWidget() {
  const { t } = useTranslation();
  const { data: clients, isLoading } = useClients();

  const stat = useMemo(() => {
    const all = clients ?? [];
    const companies = all.filter((c) => c.type === 'company').length;
    const individuals = all.filter((c) => c.type === 'individual').length;
    return { companies, individuals, total: all.length };
  }, [clients]);

  return (
    <Widget title={t('dashboard.clientsByType')} description={t('dashboard.clientsByTypeHelp')} isLoading={isLoading}>
      <MiniDonut
        centerLabel={fmtCount(stat.total)}
        slices={[
          { label: t('clients.company'),    value: stat.companies,   color: 'oklch(0.62 0.18 260)' },
          { label: t('dashboard.individual'), value: stat.individuals, color: 'oklch(0.76 0.14 70)' },
        ]}
      />
    </Widget>
  );
}

// ---- Total clients + new this month ----

export function ClientsTotalWidget() {
  const { t } = useTranslation();
  const { data: clients, isLoading } = useClients();

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const all = clients ?? [];
    return {
      total: all.length,
      newThisMonth: all.filter((c) => c.createdAt >= monthStart).length,
    };
  }, [clients]);

  return (
    <Widget title={t('dashboard.clientsTotal')} description={t('dashboard.clientsTotalHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount(stat.total)}
        sub={t('dashboard.newThisMonth', { count: stat.newThisMonth })}
      />
    </Widget>
  );
}

// ---- Total suppliers ----

export function SuppliersTotalWidget() {
  const { t } = useTranslation();
  const { data: suppliers, isLoading } = useSuppliers();

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const all = suppliers ?? [];
    return {
      total: all.length,
      newThisMonth: all.filter((s) => s.createdAt >= monthStart).length,
    };
  }, [suppliers]);

  return (
    <Widget title={t('dashboard.suppliersTotal')} description={t('dashboard.suppliersTotalHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount(stat.total)}
        sub={t('dashboard.newThisMonth', { count: stat.newThisMonth })}
      />
    </Widget>
  );
}

// ---- Representatives count ----

export function RepresentativesWidget() {
  const { t } = useTranslation();
  const { data: reps, isLoading } = useRepresentatives();

  return (
    <Widget title={t('dashboard.representativesStat')} description={t('dashboard.representativesHelp')} isLoading={isLoading}>
      <BigStat value={fmtCount((reps ?? []).length)} />
    </Widget>
  );
}

// ==================== CAISSE ====================

// ---- Open cash sessions right now ----

export function OpenSessionsWidget() {
  const { t } = useTranslation();
  const { data: open, isLoading } = useOpenCashSessions();

  return (
    <Widget title={t('dashboard.openSessions')} description={t('dashboard.openSessionsHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount((open ?? []).length)}
        tone={(open ?? []).length > 0 ? 'positive' : 'neutral'}
        sub={(open ?? []).length === 0 ? t('dashboard.noOpenSessions') : undefined}
      />
    </Widget>
  );
}

// ---- Sessions this month + cash difference total ----

export function SessionsThisMonthWidget() {
  const { t } = useTranslation();
  const { data: sessions, isLoading } = useCashSessions();

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const closed = (sessions ?? []).filter((s) => s.isClosed && (s.closedAt ?? '') >= monthStart);
    const totalDiff = closed.reduce((sum, s) => sum + (s.difference ?? 0), 0);
    return { count: closed.length, totalDiff };
  }, [sessions]);

  return (
    <Widget title={t('dashboard.sessionsThisMonth')} description={t('dashboard.sessionsThisMonthHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount(stat.count)}
        sub={stat.count > 0
          ? `${t('dashboard.totalDifference')}: ${stat.totalDiff >= 0 ? '+' : ''}${fmtMoney(stat.totalDiff, 3)} DT`
          : undefined}
        tone={Math.abs(stat.totalDiff) > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}

// ==================== WITHHOLDING ====================

// ---- Withholding total this year ----

export function WithholdingYtdWidget() {
  const { t } = useTranslation();
  const { data: certificates, isLoading } = useWithholdingCertificates({ certificateType: 'sale' });

  const stat = useMemo(() => {
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const ytd = (certificates ?? []).filter((c) => c.datePayment >= yearStart);
    return {
      total: ytd.reduce((s, c) => s + c.totalRas, 0),
      count: ytd.length,
    };
  }, [certificates]);

  return (
    <Widget title={t('dashboard.withholdingYtd')} description={t('dashboard.withholdingYtdHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.certificatesCount', { count: stat.count })}
      />
    </Widget>
  );
}

// ---- Pending declarations (not exported) ----

export function PendingDeclarationsWidget() {
  const { t } = useTranslation();
  const { data: decls, isLoading } = useWithholdingDeclarations();

  const stat = useMemo(() => {
    const notExported = (decls ?? []).filter((d) => !d.isExported);
    return { count: notExported.length };
  }, [decls]);

  return (
    <Widget title={t('dashboard.pendingDeclarations')} description={t('dashboard.pendingDeclarationsHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtCount(stat.count)}
        tone={stat.count > 0 ? 'warning' : 'positive'}
        sub={stat.count === 0 ? t('dashboard.allDeclarationsExported') : undefined}
      />
    </Widget>
  );
}
